import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Phone, Calendar, Clock, Users,
  CheckCircle, XCircle,
  Sparkles, Zap, ArrowRight, Play, Volume2, Bot, MessageSquare
} from 'lucide-react';
import { CodeText } from './TextAnimations';
import { useTranslation } from 'react-i18next';

// Section Hero améliorée avec stats - Glassmorphism Premium
export const EnhancedHeroStats = () => {
  const { t } = useTranslation();
  const stats = [
    { value: "+30", suffix: "%", labelKey: "hero.stats.rdv", color: "from-cyan-500/10 to-sky-600/10" },
    { value: "-50", suffix: "%", labelKey: "hero.stats.missed", color: "from-indigo-500/10 to-violet-600/10" },
    { value: "+25", suffix: "%", labelKey: "hero.stats.revenue", color: "from-violet-500/10 to-purple-600/10" }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.8, duration: 0.6 }}
      className="mt-16 max-w-4xl mx-auto"
    >
      {/* Tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="text-lg md:text-xl text-gray-300 text-center mb-12 leading-relaxed max-w-3xl mx-auto"
      >
        {t('hero.callup.tagline').split('\\n')[0]}<br />
        {t('hero.callup.tagline').split('\\n')[1]}
      </motion.p>

      {/* Stats Grid - Glassmorphism */}
      <div className="grid md:grid-cols-3 gap-6">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="relative group"
          >
            <motion.div
              className={`p-6 rounded-2xl bg-gradient-to-br ${stat.color} backdrop-blur-2xl relative overflow-hidden border border-white/10 shadow-2xl`}
              whileHover={{ 
                boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(139, 92, 246, 0.2)',
                borderColor: 'rgba(255,255,255,0.3)'
              }}
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%)'
              }}
            >
              {/* Inner glow */}
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.08) 0%, transparent 60%)',
                  opacity: 0.4
                }}
              />
              
              <div className="relative z-10 text-center">
                <motion.div
                  className="text-4xl md:text-5xl font-black text-white mb-2"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 + 0.3, type: "spring", stiffness: 200 }}
                  style={{
                    textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 30px rgba(255,255,255,0.15)',
                    letterSpacing: '0.02em'
                  }}
                >
                  {stat.value}{stat.suffix}
                </motion.div>
                <p className="text-white text-sm font-semibold tracking-wide">{t(stat.labelKey)}</p>
              </div>

            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Enhanced CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.5, duration: 0.6 }}
        className="flex flex-wrap gap-4 justify-center mt-12"
      >
        <motion.button
          className="px-8 py-4 bg-gradient-to-r from-sky-400 to-purple-500 text-white font-bold rounded-full inline-flex items-center gap-2 relative overflow-hidden group"
          whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(56, 189, 248, 0.6)' }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            const section = document.getElementById('trial');
            if (section) section.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <motion.span
            className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20"
            animate={{ x: [-100, 100] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <Zap className="w-5 h-5" />
          <span>{t('hero.callup.testCta')}</span>
          <ArrowRight className="w-5 h-5" />
        </motion.button>

        <motion.button
          className="px-8 py-4 border-2 border-pink-400 text-pink-400 font-bold rounded-full inline-flex items-center gap-2 relative overflow-hidden group hover:bg-pink-500/10"
          whileHover={{ scale: 1.05, borderColor: '#f472b6' }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.open('https://calendly.com/labsagency', '_blank')}
        >
          <MessageSquare className="w-5 h-5" />
          <span>{t('hero.callup.expertCta')}</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// Section Problème - Glassmorphism Premium
export const ProblemSection = () => {
  const { t } = useTranslation();
  const problems = [
    { icon: Phone, textKey: "problems.missed_calls", color: "from-cyan-500/10 to-sky-600/10", badgeKey: "problems.loss_ca" },
    { icon: Clock, textKey: "problems.unavailability", color: "from-indigo-500/10 to-violet-600/10", badgeKey: "problems.frustrated" },
    { icon: Calendar, textKey: "problems.bad_rdv", color: "from-violet-500/10 to-purple-600/10", badgeKey: "problems.disorganization" },
    { icon: Users, textKey: "problems.no_followup", color: "from-purple-600/10 to-fuchsia-600/10", badgeKey: "problems.lost_opps" }
  ];

  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Static background shapes */}
      <div className="absolute top-10 left-10 w-96 h-96 rounded-full bg-sky-500/3 blur-3xl" />
      <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-purple-500/3 blur-3xl" />

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-white tracking-tight"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            style={{
              textShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          >
            {t('problems.title')}
          </motion.h2>
          <p className="text-gray-300 text-xl font-medium tracking-wide">{t('problems.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {problems.map((problem, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0, duration: 0.3 }}
              whileHover={{ y: -10, scale: 1.05 }}
              className="relative group"
            >
              <div className={`p-6 rounded-2xl bg-gradient-to-br ${problem.color} backdrop-blur-2xl relative overflow-hidden border border-white/10 shadow-2xl`}
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%)'
                }}
              >
                <motion.div
                  className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10"
                  transition={{ duration: 0.3 }}
                />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center mb-4 mx-auto border border-white/20">
                    <problem.icon className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                  
                  {/* Badge explicite avec icône - Personnalisé */}
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div className="px-4 py-1.5 bg-red-500/20 backdrop-blur-sm rounded-full border border-red-500/40 flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-300" />
                      <span className="text-red-200 text-xs font-bold tracking-wide">{t(problem.badgeKey)}</span>
                    </div>
                  </div>
                  
                  <p className="text-white font-bold text-center tracking-wide leading-relaxed">{t(problem.textKey)}</p>
                </div>

                {/* Particules supprimées pour performance */}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Section CallUp Exemple Réel avec simulation
export const CallUpExampleSection = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    { textKey: "how_it_works.step1", icon: Phone },
    { textKey: "how_it_works.step2", icon: Bot },
    { textKey: "how_it_works.step3", icon: Sparkles },
    { textKey: "how_it_works.step4", icon: Calendar },
    { textKey: "how_it_works.step5", icon: CheckCircle },
    { textKey: "how_it_works.step6", icon: MessageSquare }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 1000); // Rapide : 1s
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 px-4 relative">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            <CodeText text={t('how_it_works.title')} className="gradient-callup" delay={0} />
          </h2>
          <p className="text-gray-300 text-xl font-medium">{t('how_it_works.subtitle')}</p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 relative z-10">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center"
              >
                <motion.div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${
                    i <= currentStep
                      ? 'bg-gradient-to-br from-sky-500 to-purple-500'
                      : 'bg-white/10'
                  }`}
                  animate={{
                    scale: i === currentStep ? [1, 1.2, 1] : 1,
                    boxShadow: i === currentStep 
                      ? ['0 0 0px rgba(14, 165, 233, 0)', '0 0 30px rgba(14, 165, 233, 0.8)', '0 0 0px rgba(14, 165, 233, 0)']
                      : 'none'
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <step.icon className="w-7 h-7 text-white" />
                </motion.div>
                <p className="text-sm text-center text-gray-300 font-medium">{t(step.textKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-4 justify-center mt-16"
        >
          <motion.button
            className="px-6 py-3 bg-gradient-to-r from-sky-500 to-purple-500 text-white font-bold rounded-full inline-flex items-center gap-2"
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(14, 165, 233, 0.6)' }}
            onClick={() => {
              const section = document.getElementById('trial');
              if (section) section.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Play className="w-5 h-5" />
            {t('how_it_works.test_cta')}
          </motion.button>

          <motion.button
            className="px-6 py-3 border-2 border-white/20 text-white font-bold rounded-full inline-flex items-center gap-2 hover:bg-white/5"
            whileHover={{ scale: 1.05 }}
            onClick={() => {
              const section = document.getElementById('trial');
              if (section) section.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Volume2 className="w-5 h-5" />
            {t('how_it_works.listen_cta')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// Je continue avec les autres sections dans le prochain message...
